import { chartFromTrack, faseSpec } from '../core/track-chart';
import type { FaseOverrides, TrackCalendar, TrackSong } from '../core/track-chart';
import type { Beat, SectionName, Seconds } from '../core/types';
import { BEATS_PER_BAR } from './plan';

export type MusicFormat = 'webm' | 'mp3';

export type MusicTrack = {
  id: string;
  world: number;
  worldName: string;
  fase: number;
  faseName: string;
  boss: boolean;
  template: string;
  spec: string;
  seed: string;
  scoreId: string;
  bpm: number;
  bars: number;
  seconds: Seconds;
  duration: Seconds;
  onset: Seconds;
  level: number;
  overrides: FaseOverrides;
  lufs: number;
  truePeak: number;
  urls: Record<MusicFormat, string>;
  calendar: string;
};

export type MusicManifest = { version: 1; lufs: number; truePeak: number; tracks: MusicTrack[] };

export type LoadedTrack = { entry: MusicTrack; calendar: TrackCalendar; buffer: AudioBuffer; format: MusicFormat };

export type TrackClock = {
  readonly beatZero: Seconds;
  readonly secondsPerBeat: Seconds;
  timeOf(beat: Beat): Seconds;
  beatAt(time: Seconds): Beat;
  sectionAt(beat: Beat): SectionName | null;
  kickPulse(at: Seconds): number;
};

export type TrackPlayback = TrackClock & { readonly startAt: Seconds; stop(when?: Seconds): void };

type Fetcher = (url: string) => Promise<Response>;
type Decoder = { decodeAudioData(data: ArrayBuffer): Promise<AudioBuffer> };
type Player = Decoder & { readonly currentTime: number; createBufferSource(): AudioBufferSourceNode };

const START_DELAY = 0.2;
const KICK_DECAY = 0.16;
const PULSE_KINDS = new Set(['kick', 'drop', '808']);

export function musicFormats(canPlay: (type: string) => string): MusicFormat[] {
  return canPlay('audio/webm; codecs="opus"') === '' ? ['mp3'] : ['webm', 'mp3'];
}

export async function loadManifest(base = '', fetcher: Fetcher = fetch): Promise<MusicManifest> {
  const res = await fetcher(`${base}music/manifest.json`);
  if (!res.ok) throw new Error(`music manifest: HTTP ${String(res.status)}`);
  return (await res.json()) as MusicManifest;
}

export function trackOf(manifest: MusicManifest, id: string): MusicTrack {
  const entry = manifest.tracks.find((t) => t.id === id);
  if (entry === undefined) throw new Error(`no music track ${id}`);
  return entry;
}

export async function loadTrack(
  entry: MusicTrack,
  ctx: Decoder,
  { base = '', fetcher = fetch, formats = ['webm', 'mp3'] }: { base?: string; fetcher?: Fetcher; formats?: readonly MusicFormat[] } = {},
): Promise<LoadedTrack> {
  const cal = await fetcher(base + entry.calendar);
  if (!cal.ok) throw new Error(`calendar ${entry.id}: HTTP ${String(cal.status)}`);
  const calendar = (await cal.json()) as TrackCalendar;
  const failures: string[] = [];
  for (const format of formats) {
    try {
      const res = await fetcher(base + entry.urls[format]);
      if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
      return { entry, calendar, buffer: await ctx.decodeAudioData(await res.arrayBuffer()), format };
    } catch (e) {
      failures.push(`${format}: ${String(e)}`);
    }
  }
  throw new Error(`music ${entry.id} not playable (${failures.join('; ')})`);
}

export function chartOfTrack(track: { entry: MusicTrack; calendar: TrackCalendar }): TrackSong {
  return chartFromTrack(track.entry.seed, track.calendar, faseSpec(track.entry.level, track.entry.overrides));
}

export function trackClock(song: TrackSong, calendar: TrackCalendar, startAt: Seconds): TrackClock {
  const secondsPerBeat = 60 / song.bpm;
  const beatZero = startAt + song.lead;
  const timeOf = (beat: Beat): Seconds => beatZero + beat * secondsPerBeat;
  const kicks = calendar.accents.filter((a) => PULSE_KINDS.has(a.kind)).map((a) => timeOf(a.beat / song.pulse));
  return {
    beatZero,
    secondsPerBeat,
    timeOf,
    beatAt: (time) => (time - beatZero) / secondsPerBeat,
    sectionAt(beat) {
      if (beat < 0 || beat >= song.endBeat) return null;
      let name: SectionName | null = null;
      for (const part of song.form) if (part.bar * BEATS_PER_BAR <= beat + 1e-6) name = part.name;
      return name;
    },
    kickPulse(at) {
      let last = -1;
      for (const k of kicks) {
        if (k > at) break;
        last = k;
      }
      return last < 0 ? 0 : Math.exp(-(at - last) / KICK_DECAY);
    },
  };
}

export function playTrack(track: LoadedTrack, song: TrackSong, ctx: Player, destination: AudioNode, when?: Seconds): TrackPlayback {
  const startAt = when ?? ctx.currentTime + START_DELAY;
  const source = ctx.createBufferSource();
  source.buffer = track.buffer;
  source.connect(destination);
  source.onended = () => {
    source.disconnect();
  };
  source.start(startAt);
  return {
    ...trackClock(song, track.calendar, startAt),
    startAt,
    stop(at) {
      source.stop(Math.max(at ?? ctx.currentTime, startAt));
    },
  };
}
